import { useState } from 'react';

import { H2, H5 } from '@common/headline';
import {
  FAQProps,
  FAQsGroupScroll,
  FAQsGroupSelector,
  FAQsList,
} from './FAQsSection';
import { ListItemFAQs } from '@common/listItem';

const medicationSupportedProgrammeBenefits = [
  'GLP-1 medication prescribed by a pharmacist, delivered discreetly to your door on a monthly basis',
  'Our premium recipe book containing hundreds of delicious and filling recipes created by our team of nutritionists, free of charge',
  'Progress tracking in our app - weight, steps, habits, waist measurement',
  "A dedicated Health Coach, who's a nutritionist or dietitian, to support you every step of the way",
  'Peer support through your personal group and specialised communities',
  'Daily educational articles on health and nutrition with activities to help you make long-term changes. These are used and backed by the NHS',
  'Access to a pharmacist to help you with your medication and assist with any potential questions/side effects',
];

export const AboutTheProgramme = [
  {
    question: 'How much weight will I lose?',
    answer:
      'Every weight loss journey is different, so results will vary depending on each individual. GLP-1 medications, like semaglutide, have been shown to support weight loss of 10-20% in body weight over the course of a year.',
  },
  {
    question:
      "What's included in Second Nature's medication-supported programme?",
    answer: (
      <span>
        <ul className="pl-2">
          {medicationSupportedProgrammeBenefits.map(benefit => (
            <ListItemFAQs key={benefit}>{benefit}</ListItemFAQs>
          ))}
        </ul>
      </span>
    ),
  },
  {
    question:
      "Why do I need a lifestyle change, can't I just take the medication?",
    answer: (
      <span>
        GLP-1 medications can be incredibly effective at curbing your appetite
        and helping you to lose weight.
        <br />
        <br />
        However, research on GLP-1s has shown that weight regain is very common
        once the medication has been stopped if the person hasn{"'"}t
        implemented lifestyle changes.
        <br />
        <br />
        That{"'"}s where Second Nature comes in. We work with you to establish
        healthier habits that will help you maintain your weight loss for the
        long term.
        <br />
        <br />
        We understand that taking weight loss medications can be expensive and
        demoralising if you become dependent on them.
        <br />
        <br />
        We{"'"}ve been working in the NHS since 2017, supporting people to lose
        weight and manage their type 2 diabetes.
        <br />
        <br />
        In 2022, the NHS{' '}
        <span className="font-medium text-blue">
          <a
            href="https://pubmed.ncbi.nlm.nih.gov/35504697/"
            rel="noreferrer"
            target="_blank"
          >
            published data
          </a>
        </span>{' '}
        comparing five different providers of the Digital Diabetes Prevention
        Programme.{' '}
        <span className="font-medium">
          The results demonstrated that Second Nature was more than twice as
          effective at supporting weight loss after 12 months than the four
          other providers.
        </span>
      </span>
    ),
  },
  {
    question:
      'Will I still need to follow a diet and exercise plan while using GLP-1 medication?',
    answer:
      "Yes, it's important to follow a healthy diet and exercise plan while using the medication to maximise weight loss and improve overall health.",
  },
  {
    question:
      'How long is the programme? Do I need to commit to the full 12 months?',
    answer: (
      <span>
        Our medication-supported programme has been carefully designed and
        optimised as a 12-month programme. The clinical trials on GLP-1
        medication that have shown the average 10-20% weight loss whilst on
        these medications and taking part in a weight-loss programme have also
        been carried out over this amount of time. If you stop sooner than this
        it{"'"}s likely that you{"'"}ll experience a lower weight loss % than
        this.
        <br />
        <br />
        It{"'"}s also important to note though that everyone{"'"}s weight loss
        journey is different, some people will start to see progress in the
        first month, whereas others won{"'"}t see progress until 2-3 months in.
        And the rate of weight-loss for each person will differ after that. You
        can stay on the medication for up to two years if it{"'"}s needed, and
        we would support you through this. However, the programme content itself
        is 12 months long.
        <br />
        <br />
        This being said, we know that in some cases there may be unexpected
        reasons (e.g. financial) that you are unable to continue for this amount
        of time, so we do allow our members to cancel at anytime. You don{"'"}t
        have to commit to the full 12-months. Depending on the dosage that you
        {"'"}re on when you cancel, the prescriber may suggest a schedule that
        extends past that cancellation month to off-board you from the
        medication safely.
      </span>
    ),
  },
];

const FAQs: Record<string, FAQProps[]> = {
  'About the medication': [
    {
      question: 'What is Wegovy?',
      answer: (
        <span>
          Wegovy is a weight-loss injection manufactured by the pharmaceutical
          company Novo Nordisk. The active ingredient in Wegovy is semaglutide.
          <br />
          <br />
          Semaglutide is in a class of medications called GLP-1 receptor
          agonists.
        </span>
      ),
    },
    {
      question: 'How does Wegovy work?',
      answer: (
        <span>
          Wegovy works by mimicking the hormone GLP-1 that interacts with the
          brain{"'"}s appetite control centre, the hypothalamus, to lower
          food-seeking behaviours and hunger.
          <br />
          <br />
          By lowering hunger, we{"'"}re able to eat less and lose weight. You
          can read our full guide for a more detailed analysis of how GLP-1
          medications like Wegovy work{' '}
          <a
            className="font-medium text-blue"
            href="https://www.secondnature.io/guides/lifestyle/glp-1-receptor-agonists"
            rel="noreferrer"
            target="_blank"
          >
            here
          </a>
          .
          <br />
          <br />
          Wegovy has additional mechanisms which support the management of blood
          sugar and insulin action, improving overall health.
        </span>
      ),
    },
    {
      question: 'How much weight can you lose with Wegovy?',
      answer: (
        <span>
          Data from{' '}
          <a
            className="font-medium text-blue"
            href="https://www.nejm.org/doi/full/10.1056/NEJMoa2032183"
            rel="noreferrer"
            target="_blank"
          >
            randomised controlled trials
          </a>{' '}
          suggests that you can lose up to 16% of your body weight on Wegovy
          after one year. If you weighed 100kg, that would mean losing 16kg.
        </span>
      ),
    },
    {
      question: 'How long will it take to lose weight on Wegovy?',
      answer: (
        <span>
          According to&nbsp;
          <a
            className="font-medium text-blue"
            href="https://www.nejm.org/doi/full/10.1056/NEJMoa2032183"
            rel="noreferrer"
            target="_blank"
          >
            randomised controlled trials
          </a>
          , you can start losing weight immediately after taking Wegovy. After
          four weeks, the average weight loss is around 2% of body weight; after
          eight weeks, it{"'"}s over 4%.
          <br />
          <br />
          Interestingly, the initial weight loss is no different from other
          interventions to support weight loss, like diet and exercise without
          Wegovy.
          <br />
          <br />
          However, Wegovy is a long-acting medication, and the real impact is
          seen beyond three months.
        </span>
      ),
    },
    {
      question: 'Do you lose more weight on Ozempic or Wegovy?',
      answer: (
        <span>
          Based on data from&nbsp;
          <a
            className="font-medium text-blue"
            href="https://pubmed.ncbi.nlm.nih.gov/34293304/"
            rel="noreferrer"
            target="_blank"
          >
            randomised controlled trials
          </a>
          , Wegovy appears to lead to more weight loss than Ozempic.
          <br />
          <br />
          Research shows Wegovy supports average weight loss of around 16% after
          one year and around 12-13% on Ozempic.
          <br />
          <br />
          Ozempic and Wegovy are the same drug (semaglutide), but Wegovy is
          available in higher doses than Ozempic.
        </span>
      ),
    },
    {
      question: 'How much does Wegovy cost?',
      answer: (
        <span>
          The cost of our Wegovy weight-loss programme depends on the dose of
          Wegovy medication you{"'"}re on. For the lower doses 0.25 mg and 0.5
          mg, the programme costs £229. If you{"'"}re on the 1 mg or 1.7 mg dose
          it costs £269 and if you{"'"}re on the highest dose 2.4 mg it costs
          £299. Note that these prices include 1-1 support from a registered
          nutritionist or dietitian, as well as our our award-winning lifestyle
          change programme that will help you adopt healthy habits so you can
          keep the weight off for good.
        </span>
      ),
    },
    {
      question: 'Is Wegovy the same as Mounjaro?',
      answer: (
        <span>
          Mounjaro and Wegovy aren{"'"}t the same. The active ingredient in
          Mounjaro is tirzepatide, and semaglutide in Wegovy. However, they do
          share some similarities.
          <br />
          <br />
          Mounjaro and Wegovy are GLP-1 receptor agonists, weight-loss
          injections that reduce appetite to support weight loss and improve
          blood glucose control through various mechanisms.
          <br />
          <br />
          GLP-1 is a hormone that lowers hunger and food-seeking behaviours by
          communicating with the brain{"'"}s appetite control centre, the
          hypothalamus. Mounjaro and Wegovy mimic the actions of GLP-1.
          <br />
          <br />
          Mounjaro, in addition to being a GLP-1 receptor agonist, also mimics a
          hormone called GIP. GIP is a hormone in the body that supports blood
          glucose levels and helps our fat cells store more excess energy we
          might consume in our diets.
          <br />
          <br />
          The main difference between Mounjaro and Wegovy is that Mounjaro
          mimics two hormones while Wegovy only mimics one.
          <br />
          <br />
          Both medications have similar dosing schedules and are administered
          once a week. The main difference is the amount of medication used. The
          volume of the doses with Mounjaro is larger than with Wegovy.
        </span>
      ),
    },
    {
      question: 'Can you get Wegovy on the NHS?',
      answer: (
        <span>
          You can Wegovy on the NHS if you{"'"}re a part of a tier 3 weight
          management service and meet other eligibility criteria.
          <br />
          <br />
          If you{"'"}re unsure about how to access weight management services in
          your area, speak to your local GP practice, who{"'"}ll be able to
          provide you with more information.
        </span>
      ),
    },
    {
      question: "What's the best diet with Wegovy?",
      answer: (
        <span>
          The best diet to eat while on Wegovy is one based primarily on whole
          foods that limit the consumption of ultra-processed foods rich in
          refined carbohydrates and sugar.
          <br />
          <br />
          On Second Nature, you{"'"}ll have the support of a registered
          dietitian or nutritionist to help you make sustainable improvements to
          your eating habits.
        </span>
      ),
    },
    {
      question: 'Is Wegovy safe?',
      answer: (
        <span>
          Wegovy and other GLP-1 medications are generally safe for most
          individuals. In a{' '}
          <a
            className="font-medium text-blue"
            href="https://www.nejm.org/doi/full/10.1056/NEJMoa2032183"
            rel="noreferrer"
            target="_blank"
          >
            recent trial
          </a>
          , 9.8% of participants out of 1,171 on Wegovy experienced serious
          adverse events.
          <br />
          <br />
          Serious adverse events are those that hinder the individual{"'"}s
          quality of life and often lead to the discontinuation of the
          medication.
          <br />
          <br />
          The majority of patients on Wegovy experience mild or no side effects
          from the medication.
        </span>
      ),
    },
    {
      question: 'Does Wegovy have side effects?',
      answer: (
        <span>
          As with all medications, semaglutide comes with some potential side
          effects, and everyone will always respond differently.
          <br />
          <br />
          These should be mild for most people and tend to resolve over time as
          your body adjusts to the medication and dose.
          <br />
          <br />
          The most common side effects of Wegovy are those related to the GI
          tract, such as nausea, diarrhoea, and constipation.
          <br />
          <br />
          It{"'"}s also common for some individuals to experience fatigue and
          lethargy on GLP-1 medications.
          <br />
          <br />
          However, these side effects are typically transient and will ease as
          you adjust to the medication.
          <br />
          <br />
          It can be hard at times to cope with these side effects, but we{"'"}re
          here to support you through your journey and will guide you every step
          of the way.
        </span>
      ),
    },
    {
      question: 'How do I get a Wegovy prescription with Second Nature?',
      answer: (
        <span>
          You{"'"}ll need to complete a consultation form that will be reviewed
          by the clinical team. If you meet our eligibility criteria, you{"'"}ll
          receive Wegovy in the post and start your Second Nature journey.
        </span>
      ),
    },
    {
      question: 'How do I take Wegovy?',
      answer: (
        <span>
          You{"'"}ll inject the medication underneath the skin using an
          injection pen (a tiny needle - like a pinprick). You should inject
          into abdominal fat, thigh, or upper arm - ideally, you should rotate
          between these locations.
          <br />
          <br />
          This is a painless and quick process, and we{"'"}ll guide you through
          how to do this! Regardless of the dose, you{"'"}ll only inject Wegovy
          once per week.
          <br />
          <br />
          Injections should be administered on the same day each week. These can
          be done at any time of day, with or without food.
        </span>
      ),
    },
    {
      question: 'What is the dosing schedule of Wegovy?',
      answer: (
        <span>
          Wegovy treatment starts with a 0.25 mg dose once a week. This dose
          then increases every month for five months until you reach the
          maintenance dose of 2.4 mg.
        </span>
      ),
    },
    {
      question: 'What if I miss a dose of Wegovy?',
      answer: (
        <span>
          If you forget to take a dose, you should inject as soon as you
          remember, as long as it{"'"}s within five days of the missed dose. You
          can then take your next dose as planned.
          <br />
          <br />
          For example, if you usually inject on a Monday, you have up to Friday
          to take your missed dose.
          <br />
          <br />
          If more than five days have passed since you forgot to take a dose,
          skip that injection and wait for the next injection day to come
          around.
          <br />
          <br />
          If you miss two consecutive weeks of doses, you should reach out to
          your health coach before restarting.
        </span>
      ),
    },
    {
      question: 'Can I be on other medications at the same time?',
      answer: (
        <span>
          There are some known drugs that interfere with semaglutide. During
          your consultation, we{"'"}ll ask you to confirm all medications you
          {"'"}re currently taking. We{"'"}ll then advise on whether you{"'"}re
          eligible to take Wegovy whilst on other medications.
        </span>
      ),
    },
    {
      question: 'How do I store the medication?',
      answer: (
        <span>
          The injection pens should be stored in a fridge. Don{"'"}t freeze
          them.
        </span>
      ),
    },
    {
      question: 'Can I travel with my injection pens?',
      answer: (
        <span>
          Yes - absolutely! You can travel through airports with your
          medication. You need to keep it in the box with the dispensing label
          clearly visible.
          <br />
          <br />
          This is so that airport security can verify that the medicine has been
          dispensed by a registered pharmacy in your name.
          <br />
          <br />
          If you have any issues, please contact{' '}
          <a
            className="font-medium text-blue"
            href="mailto:medication@secondnature.io"
            rel="noreferrer"
            target="_blank"
          >
            medication@secondnature.io
          </a>
          , and they{"'"}ll be able to support you.
        </span>
      ),
    },
  ],
  'About the programme': AboutTheProgramme,
};

export const FAQsMedicallySupported = (): JSX.Element => {
  const FAQCategories = Object.keys(FAQs);
  const [activeGroup, setActiveGroup] = useState(FAQCategories[0]);

  const selectedCategory = FAQs[activeGroup];

  return (
    <div className="bg-mono-white pb-20 pt-[60px] lg:pb-28 lg:pt-32">
      <div className="flex w-full flex-col items-center">
        <div className="text-center text-blue">
          <H2>Got questions?</H2>
          <div className="mt-4">
            <H5>Great. We{"'"}ve got answers!</H5>
          </div>
        </div>
      </div>
      <div className="mt-[52px] lg:hidden">
        <div className="bg-purple-200 text-blue-400">
          <FAQsGroupScroll
            activeGroup={activeGroup}
            groupNames={FAQCategories}
            setActiveGroup={setActiveGroup}
          />
        </div>
      </div>
      <div className="flex w-full justify-center px-3 lg:px-[60px]">
        <div className="flex w-full justify-center lg:w-[1124px] lg:justify-between">
          <div className="mt-28 hidden lg:block">
            <div className="w-[248px]">
              <FAQsGroupSelector
                activeGroup={activeGroup}
                groupNames={FAQCategories}
                setActiveGroup={setActiveGroup}
              />
            </div>
          </div>
          <div className="mt-12 w-[351px] bg-mono-white text-blue md:w-[540px] lg:ml-28 lg:mt-32 lg:w-full">
            <FAQsList FAQs={selectedCategory} />
          </div>
        </div>
      </div>
    </div>
  );
};
